import type { CreateForexOrderPayload } from "service/moneeflo/legacy-forex-create-api";
import type { DeliveryFormValues, TravelBookingFormValues } from "./schemas/travel-booking";

/** Sale unless the first product the operator added is an encashment. */
export function modeOf(values: Partial<TravelBookingFormValues>): "sale" | "encashment" {
  return values?.customers?.[0]?.products?.[0]?.productType?.type === "sell" ? "encashment" : "sale";
}

const yes = (value: string | undefined) => value === "yes";
const num = (value: string | number | undefined): number | undefined => {
  if (value === undefined || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * What the wizard collected, in the shape `POST /v1/orders/forex` takes.
 *
 * No total, tax or rate is sent: the server prices the order. A rate is sent
 * only where the operator changed the one core quoted, and then with the
 * reason they gave. A sale names its product (notes or card); an encashment
 * does not, because its direction already says it.
 */
export function buildForexOrderPayload(
  values: TravelBookingFormValues,
  opportunityId?: number,
  /** A code core has already checked for this order (a sale only). */
  couponCode?: string,
): CreateForexOrderPayload {
  const customer = values.customers?.[0];
  if (!customer?.customerId) {
    throw new Error("Pick or add the customer first.");
  }
  const mode = modeOf(values);
  const d = values.declarations ?? {};

  const lines = (customer.products ?? []).flatMap((product) =>
    product.currencies.map((c) => {
      const changed =
        c.liveRate !== undefined && c.rate !== undefined && Math.abs(c.rate - c.liveRate) > 0.00005;
      return {
        productId: mode === "sale" ? Number(product.productType.value) : undefined,
        currency: String(c.currency.value).toUpperCase(),
        fxAmount: c.amount,
        rateOverride: changed ? c.rate : undefined,
        rateOverrideReason: changed ? c.rateReason?.trim() : undefined,
      };
    }),
  );

  const address = (values.deliveries as DeliveryFormValues[] | undefined)?.[0]?.addresses?.[0];
  const delivery =
    address?.address1 && address.city && address.pincode
      ? {
          line1: address.address1,
          line2: address.address2 || undefined,
          landmark: address.landmark || undefined,
          city: address.city,
          state: address.state?.label || undefined,
          postalCode: address.pincode,
          country: address.country?.label || undefined,
        }
      : undefined;

  const payerKind = (d.payerKind || "self") as CreateForexOrderPayload["payer"]["kind"];
  const destination = values.destination;

  return {
    customerId: customer.customerId,
    direction: mode,
    purposeId: mode === "sale" ? destination?.purposeOfTravel || undefined : undefined,
    destinationCountry: mode === "sale" ? destination?.destination || undefined : undefined,
    residency: (d.residency || "unknown") as CreateForexOrderPayload["residency"],
    travellerCustomerId: yes(d.travellerDifferent) ? d.travellerCustomerId || undefined : undefined,
    payer: {
      kind: mode === "sale" ? payerKind : "self",
      organizationId: ["employer", "sponsor"].includes(payerKind) ? d.payerOrganizationId || undefined : undefined,
      name: ["sponsor", "other"].includes(payerKind) ? d.payerName?.trim() || undefined : undefined,
    },
    // An employer-paid trip is one the employer pays for, so "Paid by" says it.
    employerBorne: mode === "sale" && payerKind === "employer",
    sponsorEvidenceDocumentId: mode === "sale" && payerKind === "employer" ? d.sponsorEvidenceDocumentId || undefined : undefined,
    lrsDeclaredUsd: mode === "sale" ? num(d.lrsDeclaredUsd) : undefined,
    lrsSigned: mode === "sale" && yes(d.lrsSigned),
    payment:
      mode === "sale" && d.paymentMode
        ? {
            mode: d.paymentMode,
            instrumentRef: d.paymentMode !== "cash" ? d.instrumentRef?.trim() || undefined : undefined,
            cashAmount: d.paymentMode === "cash" ? num(d.cashAmount) : undefined,
          }
        : undefined,
    encashment:
      mode === "encashment"
        ? {
            declaredOnArrival: yes(d.declaredOnArrival),
            certificateRequested: yes(d.certificateRequested),
            payoutMethod: (d.payoutMethod || "bank") as "cash" | "bank",
          }
        : undefined,
    lines,
    delivery,
    couponCode: mode === "sale" ? couponCode || undefined : undefined,
    cityId: delivery ? address?.cityId || undefined : undefined,
    trip:
      mode === "sale" && (destination?.destination || destination?.dateRange?.start_date)
        ? {
            destinationCountry: destination?.destination || undefined,
            departureAt: destination?.dateRange?.start_date || undefined,
            returnAt: destination?.dateRange?.end_date || undefined,
          }
        : undefined,
    opportunityId: opportunityId ? String(opportunityId) : undefined,
  };
}
